import { useCallback, useEffect, useState } from 'react'

export interface NetworkModerationReportDto {
  id: string
  status: string
  reason: string
  note?: string | null
  postId: string
  commentId?: string | null
  targetTenantId?: string | null
  targetUserId?: string | null
  reporterTenantId: string
  reporterUserId: string
  createdAtUtc: string
  resolution?: string | null
  postBody?: string | null
  postPharmacyDisplayName?: string | null
  commentBody?: string | null
  commentAuthorUsername?: string | null
}

export interface NetworkModerationTicketDto {
  id: string
  kind: string
  status: string
  subject: string
  body?: string | null
  reportId?: string | null
  tenantId: string
  userId: string
  resolutionMessage?: string | null
  resolutionKind?: string | null
  reviewedAtUtc?: string | null
  createdAtUtc: string
  report?: NetworkModerationReportDto | null
}

export interface NetworkBanDto {
  id: string
  tenantId: string
  userId: string
  reason?: string | null
  bannedAtUtc: string
  isActive: boolean
}

function normalizeReport(raw: unknown): NetworkModerationReportDto | null {
  if (!raw || typeof raw !== 'object') return null
  const r = raw as Record<string, unknown>
  const id = String(r.id ?? '')
  if (!id) return null
  return {
    id,
    status: String(r.status ?? 'pending'),
    reason: String(r.reason ?? ''),
    note: r.note != null ? String(r.note) : null,
    postId: String(r.postId ?? ''),
    commentId: r.commentId != null ? String(r.commentId) : null,
    targetTenantId: r.targetTenantId != null ? String(r.targetTenantId) : null,
    targetUserId: r.targetUserId != null ? String(r.targetUserId) : null,
    reporterTenantId: String(r.reporterTenantId ?? ''),
    reporterUserId: String(r.reporterUserId ?? ''),
    createdAtUtc: String(r.createdAtUtc ?? ''),
    resolution: r.resolution != null ? String(r.resolution) : null,
    postBody: r.postBody != null ? String(r.postBody) : null,
    postPharmacyDisplayName: r.postPharmacyDisplayName != null ? String(r.postPharmacyDisplayName) : null,
    commentBody: r.commentBody != null ? String(r.commentBody) : null,
    commentAuthorUsername: r.commentAuthorUsername != null ? String(r.commentAuthorUsername) : null,
  }
}

function normalizeModerationTicket(raw: unknown): NetworkModerationTicketDto | null {
  if (!raw || typeof raw !== 'object') return null
  const r = raw as Record<string, unknown>
  const id = String(r.id ?? '')
  if (!id) return null
  const reportRaw = r.report
  const report = reportRaw ? normalizeReport(reportRaw) : null
  return {
    id,
    kind: String(r.kind ?? ''),
    status: String(r.status ?? 'pending'),
    subject: String(r.subject ?? ''),
    body: r.body != null ? String(r.body) : null,
    reportId: r.reportId != null ? String(r.reportId) : null,
    tenantId: String(r.tenantId ?? ''),
    userId: String(r.userId ?? ''),
    resolutionMessage: r.resolutionMessage != null ? String(r.resolutionMessage) : null,
    resolutionKind: r.resolutionKind != null ? String(r.resolutionKind) : null,
    reviewedAtUtc: r.reviewedAtUtc != null ? String(r.reviewedAtUtc) : null,
    createdAtUtc: String(r.createdAtUtc ?? ''),
    report,
  }
}

export function usePharmacyNetworkModeration(enabled: boolean) {
  const [reports, setReports] = useState<NetworkModerationReportDto[]>([])
  const [tickets, setTickets] = useState<NetworkModerationTicketDto[]>([])
  const [pendingCount, setPendingCount] = useState(0)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const refreshPendingCount = useCallback(async () => {
    if (!enabled) return 0
    try {
      const res = await fetch('/api/opas/pharmacy-network/moderation/reports/pending-count', {
        credentials: 'include',
        cache: 'no-store',
      })
      const data = await res.json()
      if (!res.ok) return 0
      const count = Number(data.count ?? 0)
      setPendingCount(count)
      return count
    } catch {
      return 0
    }
  }, [enabled])

  const loadReports = useCallback(async (status = 'pending') => {
    if (!enabled) return
    setLoading(true)
    setError(null)
    try {
      const res = await fetch(
        `/api/opas/pharmacy-network/moderation/reports?status=${encodeURIComponent(status)}&pageSize=50`,
        { credentials: 'include', cache: 'no-store' }
      )
      const data = await res.json()
      if (!res.ok) throw new Error(data.error ?? 'Bildirimler yüklenemedi')
      const items = (data.items ?? []).map(normalizeReport).filter(Boolean) as NetworkModerationReportDto[]
      setReports(items)
      void refreshPendingCount()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Bildirimler yüklenemedi')
    } finally {
      setLoading(false)
    }
  }, [enabled, refreshPendingCount])

  const loadTickets = useCallback(async (status: 'pending' | 'resolved' = 'pending') => {
    if (!enabled) return
    setLoading(true)
    setError(null)
    try {
      const res = await fetch(
        `/api/opas/pharmacy-network/moderation/tickets?status=${encodeURIComponent(status)}&pageSize=50`,
        { credentials: 'include', cache: 'no-store' }
      )
      const data = await res.json()
      if (!res.ok) throw new Error(data.error ?? 'Talepler yüklenemedi')
      const items = (data.items ?? []).map(normalizeModerationTicket).filter(Boolean) as NetworkModerationTicketDto[]
      setTickets(items)
      void refreshPendingCount()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Talepler yüklenemedi')
    } finally {
      setLoading(false)
    }
  }, [enabled, refreshPendingCount])

  const resolveReport = useCallback(
    async (
      reportId: string,
      action: 'delete' | 'warn' | 'ban' | 'dismiss',
      note?: string,
      warningMessage?: string,
      reporterMessage?: string
    ) => {
      const res = await fetch(
        `/api/opas/pharmacy-network/moderation/reports/${encodeURIComponent(reportId)}/resolve`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            action,
            note: note ?? null,
            warningMessage: warningMessage ?? null,
            reporterMessage: reporterMessage ?? null,
          }),
        }
      )
      const data = await res.json()
      if (!res.ok) throw new Error(data.error ?? 'İşlem tamamlanamadı')
      setReports((prev) => prev.filter((r) => r.id !== reportId))
      setTickets((prev) => prev.filter((t) => t.report?.id !== reportId))
      void refreshPendingCount()
      return data.resolution as string
    },
    [refreshPendingCount]
  )

  const completeTicket = useCallback(
    async (ticketId: string, message: string) => {
      const res = await fetch(
        `/api/opas/pharmacy-network/moderation/tickets/${encodeURIComponent(ticketId)}/complete`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ message }),
        }
      )
      const data = await res.json()
      if (!res.ok) throw new Error(data.error ?? 'İşlem tamamlanamadı')
      setTickets((prev) => prev.filter((t) => t.id !== ticketId))
      void refreshPendingCount()
    },
    [refreshPendingCount]
  )

  useEffect(() => {
    if (!enabled) {
      setReports([])
      setTickets([])
      setPendingCount(0)
      return
    }
    void refreshPendingCount()
  }, [enabled, refreshPendingCount])

  return {
    reports,
    tickets,
    pendingCount,
    loading,
    error,
    loadReports,
    loadTickets,
    resolveReport,
    completeTicket,
    refreshPendingCount,
  }
}

export function usePharmacyNetworkBans(enabled: boolean) {
  const [bans, setBans] = useState<NetworkBanDto[]>([])
  const [loading, setLoading] = useState(false)

  const loadBans = useCallback(async () => {
    if (!enabled) return
    setLoading(true)
    try {
      const res = await fetch('/api/opas/pharmacy-network/moderation/bans?activeOnly=true', {
        credentials: 'include',
        cache: 'no-store',
      })
      const data = await res.json()
      if (!res.ok) return
      setBans(
        (data.items ?? []).map((b: Record<string, unknown>) => ({
          id: String(b.id ?? ''),
          tenantId: String(b.tenantId ?? ''),
          userId: String(b.userId ?? ''),
          reason: b.reason != null ? String(b.reason) : null,
          bannedAtUtc: String(b.bannedAtUtc ?? ''),
          isActive: Boolean(b.isActive),
        })).filter((b: NetworkBanDto) => b.id)
      )
    } finally {
      setLoading(false)
    }
  }, [enabled])

  const liftBan = useCallback(async (banId: string) => {
    const res = await fetch(`/api/opas/pharmacy-network/moderation/bans/${encodeURIComponent(banId)}/lift`, {
      method: 'POST',
      credentials: 'include',
    })
    const data = await res.json()
    if (!res.ok) throw new Error(data.error ?? 'Kısıtlama kaldırılamadı')
    setBans((prev) => prev.filter((b) => b.id !== banId))
  }, [])

  useEffect(() => {
    if (enabled) void loadBans()
    else setBans([])
  }, [enabled, loadBans])

  return { bans, loading, loadBans, liftBan }
}

export function usePharmacyNetworkBlocks() {
  const blockUser = useCallback(async (blockedTenantId: string, blockedUserId: string) => {
    const res = await fetch('/api/opas/pharmacy-network/blocks', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ blockedTenantId, blockedUserId }),
    })
    const data = await res.json()
    if (!res.ok) throw new Error(data.error ?? 'Engellenemedi')
    return Boolean(data.alreadyBlocked)
  }, [])

  return { blockUser }
}

export interface NetworkWarningDto {
  id: string
  message: string
  createdAtUtc: string
}

export function usePharmacyNetworkWarnings(enabled: boolean) {
  const [warnings, setWarnings] = useState<NetworkWarningDto[]>([])

  const loadWarnings = useCallback(async () => {
    if (!enabled) return
    try {
      const res = await fetch('/api/opas/pharmacy-network/warnings/pending', {
        credentials: 'include',
        cache: 'no-store',
      })
      const data = await res.json()
      if (!res.ok) return
      setWarnings(
        (data.items ?? []).map((w: Record<string, unknown>) => ({
          id: String(w.id ?? ''),
          message: String(w.message ?? ''),
          createdAtUtc: String(w.createdAtUtc ?? ''),
        })).filter((w: NetworkWarningDto) => w.id)
      )
    } catch {
      /* optional */
    }
  }, [enabled])

  const markSeen = useCallback(async (warningId: string) => {
    const res = await fetch(`/api/opas/pharmacy-network/warnings/${encodeURIComponent(warningId)}/seen`, {
      method: 'POST',
      credentials: 'include',
    })
    if (!res.ok) return
    setWarnings((prev) => prev.filter((w) => w.id !== warningId))
  }, [])

  useEffect(() => {
    if (enabled) void loadWarnings()
    else setWarnings([])
  }, [enabled, loadWarnings])

  return { warnings, loadWarnings, markSeen }
}
